import React from 'react'
import {
  Controller,
  useFieldArray,
  UseFieldArrayRemove,
  useFormContext,
} from 'react-hook-form'
import { FiltersSchema } from '../filtersSchema'
import { Box, Button, Field, Flex, Input, useAppTheme } from '@workwhile/ui'
import { CircleMinus } from 'lucide-react'

const AddedPositionRow = ({
  remove,
  index,
  id,
  isLast,
}: {
  remove: UseFieldArrayRemove
  index: number
  id: number
  isLast: boolean
}) => {
  const { colors, radii, space } = useAppTheme()
  return (
    <Flex
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
      gap={space[2]}
      mb={isLast ? 0 : space[2]}
    >
      <Flex
        alignItems="flex-start"
        justifyContent="space-between"
        border="1px solid"
        borderColor={colors.neutrals[100]}
        p={2}
        borderRadius={radii.small}
        width="100%"
        position="relative"
      >
        {id}
      </Flex>
      <Button
        onClick={() => remove(index)}
        variant="text"
        color="red"
        size="small"
        danger
        style={{
          margin: 0,
          height: '1.6rem',
        }}
      >
        <CircleMinus size={16} />
      </Button>
    </Flex>
  )
}

const AddedPositionIds = () => {
  const { watch, control } = useFormContext<FiltersSchema>()

  const { remove } = useFieldArray({
    name: 'shift.positionIds',
    control,
  })

  const positionIds = watch('shift.positionIds') ?? []

  const addedRows = positionIds.map(({ id }, index) => (
    <AddedPositionRow
      key={`added-position-${id}-${index}`}
      remove={remove}
      index={index}
      id={id}
      isLast={index === positionIds.length - 1}
    />
  ))
  return <>{addedRows}</>
}

export const PositionIdsFilter = () => {
  const { control, trigger, getValues, resetField } =
    useFormContext<FiltersSchema>()

  const { append } = useFieldArray({
    name: 'shift.positionIds',
    control,
  })

  return (
    <Box>
      <Controller
        control={control}
        name="shift.positionIdUserInput"
        render={({ field, fieldState: { error, invalid } }) => (
          <Field error={error?.message?.toString()}>
            <Flex gap={2}>
              <Input
                placeholder="e.g. 12345"
                {...field}
                value={field.value ?? ''}
                type="number"
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                mb={2}
              />
              <Button
                variant="primary"
                disabled={invalid}
                onClick={async () => {
                  const isInputValid = await trigger(
                    'shift.positionIdUserInput'
                  )

                  const positionId = Number(
                    getValues('shift.positionIdUserInput')
                  )

                  const added = getValues('shift.positionIds') ?? []
                  const existsAlready = added.some(
                    (entry) => entry.id === positionId
                  )
                  const isValid =
                    isInputValid && !existsAlready && !Number.isNaN(positionId)
                  if (isValid) {
                    append({ id: positionId })
                    resetField('shift.positionIdUserInput', {
                      defaultValue: undefined,
                    })
                  }

                  if (existsAlready) {
                    resetField('shift.positionIdUserInput', {
                      defaultValue: undefined,
                    })
                  }
                }}
              >
                Add
              </Button>
            </Flex>
          </Field>
        )}
      />
      <AddedPositionIds />
    </Box>
  )
}
